// A20 phase 1b (B6) — THE AUDIT TRAIL, MADE VISIBLE.
//
// `support_ticket_events` was WRITE-ONLY. Migration 41 built it deliberately
// append-only, with a trigger that refuses a rewrite, a comment insisting an
// unattributed transition "is not an audit record, it is a rumour" — and then
// nothing anywhere read it back. No endpoint, no service method, no surface. An
// audit trail that can only be reached from a psql prompt does not answer "who
// closed this and why", which is the only question it holds.
//
// ── WHAT THIS RENDERS, AND WHY IT BRANCHES ON `kind` ────────────────────────
// ONE table carries TWO state machines (migration 41 SECTION 1): a 'status' row
// holds ticket statuses in from_status/to_status, a 'github_sync' row holds
// github_sync_state values in the SAME two columns. One label map over both
// would print "Sync pending → Sync pending" as though it were a lifecycle move,
// so `kind` is read before anything is labelled.
//
// ── THE STATE THIS SURFACE HAS THAT THE OTHERS DO NOT ───────────────────────
// A REDACTED note. `reason` is customer payload by migration 41's own
// classification (a triage note routinely quotes the report), so
// redact_ticket_rows() and the org-erasure trigger both NULL it while leaving
// `actor`, the timestamps and the transition intact. An old row therefore comes
// back with a real transition and no reason, and rendering that as "no reason
// given" would be a small lie told forever about a note somebody did write.
// Distinguished by the row's age against the ticket's own redaction clock is
// impossible from here, so it is stated as the ambiguity it is.

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, GitBranch, RefreshCw, UserCog } from 'lucide-react';
import { formatDateTime, formatRelativeTime } from '@/lib/utils';
import { Card, CardHead } from '@/components/shared/primitives';
import { Skel } from '@/components/shared/SkeletonLoader';
import { classifyApiError } from '@/components/shared/StateFrame';
import { statusLabel, syncStateLabel } from './chips';
import { listTicketEvents, type TicketEvent } from './api';

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px',
  borderRadius: 8, background: 'var(--surface-raised)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; events: TicketEvent[] }
  | { kind: 'error'; message: string; unauthorized: boolean };

/** "Open → Triaged" for a lifecycle row, "Sync failed → Queued" for the other. */
function transitionWords(event: TicketEvent): { from: string | null; to: string } {
  const label = event.kind === 'github_sync' ? syncStateLabel : statusLabel;
  return { from: event.from_status ? label(event.from_status) : null, to: label(event.to_status) };
}

/**
 * WHO. Rendered verbatim, including the machine identities.
 *
 * migration 41's column comment fixes the conventions: an operator's email or
 * id, 'system:sync' for the scheduled sweep, 'github:<login>' for a transition
 * driven upstream. Prettifying those would erase the distinction between a
 * person and a cron job, which is the first thing anyone reading an audit trail
 * needs to know.
 */
function ActorLine({ actor }: { actor: string }) {
  const machine = actor.startsWith('system:') || actor.startsWith('github:');
  return (
    <span style={{ fontFamily: machine ? 'var(--mono)' : 'inherit', fontSize: machine ? 11.5 : 12.5, color: 'var(--text-secondary)' }}>
      {actor}
    </span>
  );
}

export interface TicketTimelineProps {
  ticketId: string;
  /**
   * Bumped by the page after any committed mutation. A transition that is not
   * reflected here within the same click would make the operator doubt either
   * the write or the log, and the log is the thing that has to be trusted.
   */
  refreshKey?: number;
}

export function TicketTimeline({ ticketId, refreshKey = 0 }: TicketTimelineProps) {
  const [state, setState] = useState<State>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'ready', events: await listTicketEvents(ticketId) });
    } catch (err) {
      // A failure here NEVER takes the page down — the report, the extracts and
      // the replay are all independently useful, and this is the same "partial"
      // posture StateFrame's own header prescribes: render it inline, next to
      // the data that did load, rather than as a full-surface takeover.
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'The timeline could not be read.',
        unauthorized: classifyApiError(err) === 'unauthorized',
      });
    }
  }, [ticketId]);

  // `refreshKey` is a dependency of the EFFECT, not of the callback: the reload
  // is triggered by the bump, but the function it runs does not vary with it.
  useEffect(() => { load(); }, [load, refreshKey]);

  const events = state.kind === 'ready' ? state.events : [];

  return (
    <Card>
      <CardHead
        title="Timeline"
        sub="Every accepted transition, with who made it and why. Append-only: entries cannot be edited or removed."
        right={
          state.kind === 'loading' ? undefined : (
            <button type="button" onClick={load} style={btnSec}><RefreshCw size={12} /> Refresh</button>
          )
        }
      />
      <div style={{ padding: state.kind === 'ready' && events.length ? 0 : '16px 18px' }}>
        {state.kind === 'loading' ? (
          <div style={{ display: 'grid', gap: 8 }}>
            <Skel height={38} borderRadius={8} />
            <Skel height={38} borderRadius={8} />
            <Skel height={38} borderRadius={8} />
          </div>
        ) : state.kind === 'error' ? (
          <div style={{ display: 'grid', gap: 10, justifyItems: 'start' }}>
            <div
              role="alert"
              style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: '#FEE2E2', border: '1px solid #FECACA', color: '#7F1D1D', fontSize: 12.5, lineHeight: 1.5, display: 'flex', gap: 10 }}
            >
              <AlertTriangle size={15} color="#DC2626" style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                {/* An unreadable log and an empty log are different facts and
                    must never render the same — the same rule the queue read
                    follows about "no tickets" versus "I could not read". */}
                Could not read this ticket’s history: {state.message}
                {!state.unauthorized && (
                  <div style={{ marginTop: 4 }}>
                    Nothing above is affected. If this server has not had migration 41 applied it has
                    no timeline table to read, and the lifecycle controls will say so too.
                  </div>
                )}
              </div>
            </div>
            <button type="button" onClick={load} style={btnSec}><RefreshCw size={12} /> Try again</button>
          </div>
        ) : events.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Nothing has happened to this ticket since it was filed. Every status change and every
            GitHub re-arm appends an entry here, attributed to whoever made it — so an empty timeline
            means no one has moved it, not that the record is missing.
          </div>
        ) : (
          <div>
            {events.map((event) => {
              const { from, to } = transitionWords(event);
              const sync = event.kind === 'github_sync';
              const IconCmp = sync ? GitBranch : UserCog;
              return (
                <div
                  key={event.id}
                  data-kind={event.kind}
                  style={{
                    display: 'flex', gap: 11, padding: '11px 18px',
                    borderBottom: '1px solid var(--border)', alignItems: 'flex-start',
                  }}
                >
                  <span
                    style={{
                      width: 24, height: 24, borderRadius: 7, flexShrink: 0, marginTop: 1,
                      background: sync ? 'var(--bg-muted)' : 'var(--accent-light)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <IconCmp size={13} color={sync ? 'var(--text-secondary)' : 'var(--accent)'} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                      {from ? (
                        <>
                          <span style={{ color: 'var(--text-secondary)' }}>{from}</span>
                          {' → '}
                          <strong>{to}</strong>
                        </>
                      ) : (
                        // from_status is NULL only on a hand-written correction
                        // that could not honestly name a prior state. Say that,
                        // rather than inventing an arrow out of nowhere.
                        <>
                          <strong>{to}</strong>
                          <span style={{ color: 'var(--text-muted)' }}> · no prior state recorded</span>
                        </>
                      )}
                      {sync && <span style={{ color: 'var(--text-muted)', fontSize: 11.5 }}> · GitHub sync</span>}
                    </div>
                    <div style={{ marginTop: 2 }}>
                      <ActorLine actor={event.actor} />
                      <span style={{ color: 'var(--text-muted)', fontSize: 11.5 }} title={formatDateTime(event.created_at)}>
                        {' · '}{formatRelativeTime(event.created_at)}
                      </span>
                    </div>
                    {event.reason ? (
                      <div style={{ marginTop: 5, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                        {event.reason}
                      </div>
                    ) : (
                      <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        No note — either none was given, or it was cleared by redaction.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

export default TicketTimeline;
